/**
 * @description React context for time clock session state management
 *
 * Key features:
 * - Session token management (sessionStorage)
 * - Current employee info
 * - Clock status (clocked_in, on_break, clocked_out)
 * - Session expiry handling
 * - Company config (name, timezone)
 *
 * @notes
 * - Token stored in sessionStorage (cleared on tab close)
 * - Listens for 'clockSessionExpired' custom event from apiClient
 * - Auto-refreshes status every 60 seconds while authenticated
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import clockService from '../services/clockService';

const ClockContext = createContext(null);

export const useClockContext = () => {
  const context = useContext(ClockContext);
  if (!context) {
    throw new Error('useClockContext must be used within ClockProvider');
  }
  return context;
};

export const ClockProvider = ({ companyId, children }) => {
  const [companyConfig, setCompanyConfig] = useState(null);
  const [employee, setEmployee] = useState(null);
  const [clockStatus, setClockStatus] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load company config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await clockService.getConfig(companyId);
        setCompanyConfig(config);
        setError(null);
      } catch (err) {
        setError('Unable to load company information');
      } finally {
        setLoading(false);
      }
    };
    if (companyId) loadConfig();
  }, [companyId]);

  // Check for existing session on mount
  useEffect(() => {
    const token = sessionStorage.getItem('clockSessionToken');
    const employeeData = sessionStorage.getItem('clockEmployee');
    if (token && employeeData) {
      setEmployee(JSON.parse(employeeData));
      setIsAuthenticated(true);
      refreshStatus();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for session expiry events
  useEffect(() => {
    const handleExpiry = () => {
      logout();
    };
    window.addEventListener('clockSessionExpired', handleExpiry);
    return () => window.removeEventListener('clockSessionExpired', handleExpiry);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-refresh status every 60 seconds
  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      refreshStatus();
    }, 60000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // logout defined first (no callback deps)
  const logout = useCallback(() => {
    sessionStorage.removeItem('clockSessionToken');
    sessionStorage.removeItem('clockEmployee');
    setEmployee(null);
    setClockStatus(null);
    setIsAuthenticated(false);
  }, []);

  // refreshStatus defined second (depends on logout)
  const refreshStatus = useCallback(async () => {
    try {
      const status = await clockService.getStatus(companyId);
      setClockStatus(status);
    } catch (err) {
      if (err.response?.status === 401) {
        logout();
      }
    }
  }, [companyId, logout]);

  // login defined third (depends on refreshStatus)
  const login = useCallback(async (employeeData, pin) => {
    try {
      const result = await clockService.authenticate(companyId, employeeData.id, pin);
      sessionStorage.setItem('clockSessionToken', result.token);
      sessionStorage.setItem('clockEmployee', JSON.stringify(employeeData));
      setEmployee(employeeData);
      setIsAuthenticated(true);
      setError(null);
      // Fetch initial status
      await refreshStatus();
      return { success: true };
    } catch (err) {
      const data = err.response?.data;
      return {
        success: false,
        locked: data?.locked || false,
        attemptsRemaining: data?.attemptsRemaining,
        remainingMinutes: data?.remainingMinutes,
        error: data?.error || 'Authentication failed',
      };
    }
  }, [companyId, refreshStatus]);

  // punch defined fourth (depends on refreshStatus)
  const punch = useCallback(async (action) => {
    try {
      const result = await clockService.punch(companyId, action);
      await refreshStatus();
      return { success: true, result };
    } catch (err) {
      return {
        success: false,
        error: err.response?.data?.error || 'Punch failed',
      };
    }
  }, [companyId, refreshStatus]);

  const value = {
    companyId,
    companyConfig,
    employee,
    clockStatus,
    isAuthenticated,
    loading,
    error,
    login,
    logout,
    punch,
    refreshStatus,
  };

  return (
    <ClockContext.Provider value={value}>
      {children}
    </ClockContext.Provider>
  );
};

export default ClockContext;
